<template>
  <div class="content-wrapper">
  <div class="branch-dashboard">

    <!-- ===== Header ===== -->
    <div class="d-flex justify-content-between align-items-start mb-2 flex-wrap" style="gap: 12px">
      <div>
        <!-- Reached from the clinic overview (central drill-down) — offer a way back. -->
        <b-link
          v-if="isCentralFlow"
          class="d-inline-flex align-items-center small mb-1"
          @click="goBackToClinic"
        >
          <i class="fas fa-chevron-left mr-1"></i> กลับไประดับคลินิก
        </b-link>
        <div class="font-weight-bold">
          แดชบอร์ดประจำสาขา · {{ branchName }}
        </div>
        <small class="text-muted">
          <template v-if="d">
            ข้อมูล <b class="text-dark">{{ rangeLabel }}</b>
            <span v-if="comparisonLabel">
              · เทียบกับ <span class="text-primary-dark">{{ comparisonLabel }}</span>
            </span>
          </template>
        </small>
      </div>

      <!-- ===== PeriodSelector ===== -->
      <period-selector :loading="loading" :initial="initialPeriod" @change="onPeriodChange" />
    </div>

    <!-- Loading state -->
    <div v-if="loading && !d" class="text-center p-5">
      <b-spinner />
    </div>

    <!-- Error state -->
    <div v-else-if="error" class="text-center p-5">
      <p class="text-danger">โหลดข้อมูลไม่สำเร็จ</p>
      <b-button size="sm" variant="outline-primary" @click="load">ลองใหม่</b-button>
    </div>

    <template v-else-if="d">

      <!-- ===== ROW 1: Hero — รายได้ + ฿/ใบเสร็จ + อันดับ/ค่าเฉลี่ยคลินิก ===== -->
      <b-row class="mb-2">
        <!-- รายได้รวมในช่วง (feature) -->
        <b-col md="5" class="mb-2 mb-md-0">
          <b-card class="hero-card hero-card--feat h-100" body-class="d-flex flex-column justify-content-center">
            <div class="label-feat small font-weight-semibold text-uppercase">เงินเข้าจริงในช่วง</div>
            <div class="d-flex align-items-baseline mt-1" style="gap: 6px">
              <span class="hero-num-lg">{{ formatNumber(d.hero.revenue.value) }}</span>
              <span class="unit-baht">฿</span>
            </div>
            <div class="label-feat small mt-2">
              {{ formatNumber(d.stats.receiptsIssued) }} ใบเสร็จรักษา<template
                v-if="d.stats.depositReceipts"
              > · {{ formatNumber(d.stats.depositReceipts) }} ใบมัดจำ</template>
            </div>

            <!-- ที่มาของเงินเข้าจริง — สองบรรทัดนี้บวกกันได้ยอดใหญ่ด้านบนพอดี
                 (มัดจำเก่าที่ถูกเอามาตัดค่ารักษา ดูได้ที่ช่อง "มัดจำ" ในแถบสถิติ) -->
            <div v-if="hasDepositActivity" class="hero-breakdown mt-3">
              <div>
                <span>ค่ารักษาที่เก็บเงินได้</span>
                <b>{{ formatNumber(d.hero.revenue.treatmentCash) }}</b>
              </div>
              <div>
                <span>รับเงินมัดจำ</span>
                <b>+{{ formatNumber(d.hero.revenue.depositReceived) }}</b>
              </div>
            </div>
            <div class="badges mt-3">
              <span
                v-for="(b, i) in revenueBadges"
                :key="i"
                class="hero-badge"
                :class="{ active: b.active }"
              >
                <span class="hero-badge-v" :class="b.pct >= 0 ? 'up' : 'down'">
                  {{ b.pct >= 0 ? '▲ +' : '▼ ' }}{{ Math.abs(b.pct).toFixed(1) }}%
                </span>
                <span class="hero-badge-tag">{{ b.tag }}</span>
              </span>
            </div>
            <target-progress
              class="mt-3"
              variant="full"
              dark
              :revenue-target="d.revenueTarget"
              :actual="d.hero.revenue.value"
              :start-dt="period && period.startDt"
              :end-dt="period && period.endDt"
            />
          </b-card>
        </b-col>

        <!-- ฿ ต่อใบเสร็จ -->
        <b-col md="4" class="mb-2 mb-md-0">
          <b-card class="hero-card h-100" body-class="d-flex flex-column justify-content-center">
            <div class="label-muted small font-weight-semibold text-uppercase">฿ ต่อใบเสร็จ</div>
            <div class="d-flex align-items-baseline mt-1" style="gap: 4px">
              <span class="hero-num">{{ formatNumber(d.hero.perCase.value) }}</span>
              <span class="small text-muted">฿</span>
            </div>
            <div class="label-muted small mt-2">ค่ารักษา ÷ ใบเสร็จรักษา</div>
            <div class="badges mt-3">
              <span
                v-for="(b, i) in perCaseBadges"
                :key="i"
                class="hero-badge hero-badge--plain"
                :class="{ active: b.active }"
              >
                <span class="hero-badge-v" :class="b.pct >= 0 ? 'up' : 'down'">
                  {{ b.pct >= 0 ? '▲ +' : '▼ ' }}{{ Math.abs(b.pct).toFixed(1) }}%
                </span>
                <span class="hero-badge-tag">{{ b.tag }}</span>
              </span>
            </div>

            <!-- ใช้ยูนิตเฉลี่ย — second metric in the same card (mirrors mockup) -->
            <div class="hero-divider my-3"></div>
            <div class="label-muted small font-weight-semibold text-uppercase">ใช้ยูนิตเฉลี่ย</div>
            <div class="d-flex align-items-baseline mt-1" style="gap: 4px">
              <span class="hero-num" style="font-size: clamp(20px, 2vw, 26px)">{{ unitAvgPct }}</span>
              <span class="small text-muted">%</span>
            </div>
            <div
              v-if="utilVsClinicLabel"
              class="small mt-1"
              :class="utilVsClinicLabel.cls === 'up' ? 'text-green' : 'text-red'"
            >
              {{ utilVsClinicLabel.text }} จุด
            </div>
          </b-card>
        </b-col>

        <!-- ผู้ป่วยใหม่ vs กลับมา -->
        <b-col md="3">
          <b-card class="hero-card h-100" body-class="d-flex flex-column justify-content-center">
            <div class="label-muted small font-weight-semibold text-uppercase">ผู้ป่วย</div>
            <div class="mt-2">
              <div class="d-flex justify-content-between align-items-baseline">
                <span class="small text-muted">ใหม่</span>
                <b style="font-size: clamp(18px, 2vw, 22px)">{{ formatNumber(d.stats.patients.new) }}</b>
              </div>
              <div class="d-flex justify-content-between align-items-baseline mt-1">
                <span class="small text-muted">กลับมา</span>
                <b style="font-size: clamp(16px, 1.8vw, 20px)">{{ formatNumber(d.stats.patients.total - d.stats.patients.new) }}</b>
              </div>
              <div class="mt-3" style="height: 6px; border-radius: 3px; background: #eef1f6; overflow: hidden">
                <div
                  :style="{ width: newPatientPct + '%', background: '#2196F3', height: '100%',
                            borderRadius: '3px', transition: 'width .4s ease' }"
                />
              </div>
              <div class="d-flex justify-content-between mt-1">
                <small class="text-primary font-weight-semibold">{{ newPatientPct }}% ใหม่</small>
                <small class="text-muted">{{ 100 - newPatientPct }}% กลับมา</small>
              </div>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <!-- ===== ROW 2: Stats strip — 5 ช่อง ===== -->
      <b-card class="stat-card mb-2" body-class="p-0">
        <div class="d-flex divide-cols flex-wrap">
          <!-- ผู้ป่วย -->
          <div class="stat-cell flex-fill d-flex flex-column justify-content-center px-3 py-2">
            <span class="label-muted small font-weight-semibold text-uppercase">ผู้ป่วย</span>
            <div class="d-flex align-items-baseline mt-1" style="gap: 4px">
              <span class="stat-num">{{ formatNumber(d.stats.patients.total) }}</span>
              <span class="small text-muted">คน</span>
            </div>
            <trend-badge class="mt-1" :pct="d.stats.patientsPrevPct" />
          </div>

          <!-- ผู้ป่วยใหม่ -->
          <div class="stat-cell flex-fill d-flex flex-column justify-content-center px-3 py-2">
            <span class="label-muted small font-weight-semibold text-uppercase">ผู้ป่วยใหม่</span>
            <div class="d-flex align-items-baseline mt-1" style="gap: 4px">
              <span class="stat-num text-green">{{ formatNumber(d.stats.patients.new) }}</span>
              <span class="small text-muted">คน</span>
            </div>
            <div class="small text-muted mt-1">
              เก่า {{ formatNumber(d.stats.patients.total - d.stats.patients.new) }}
            </div>
          </div>

          <!-- ใบเสร็จ -->
          <div class="stat-cell flex-fill d-flex flex-column justify-content-center px-3 py-2">
            <span class="label-muted small font-weight-semibold text-uppercase">ใบเสร็จ</span>
            <div class="d-flex align-items-baseline mt-1" style="gap: 4px">
              <span class="stat-num">{{ formatNumber(d.stats.receiptsIssued) }}</span>
            </div>
            <trend-badge class="mt-1" :pct="d.stats.receiptsPrevPct" />
          </div>

          <!-- มัดจำ — โผล่เฉพาะสาขาที่มีการรับ/ใช้มัดจำจริงในช่วงนี้ -->
          <div
            v-if="hasDepositActivity"
            class="stat-cell stat-cell--deposit flex-fill d-flex flex-column justify-content-center px-3 py-2"
          >
            <span class="small font-weight-semibold text-uppercase text-primary-dark">มัดจำ</span>
            <div class="d-flex align-items-baseline mt-1" style="gap: 4px">
              <span class="stat-num text-primary-dark">{{ formatNumber(d.hero.revenue.depositReceived) }}</span>
              <span class="small text-muted">฿ รับ</span>
            </div>
            <div class="small text-muted mt-1">
              ใช้ไป {{ formatNumber(d.hero.revenue.depositUsed) }} ฿
            </div>
          </div>

          <!-- No-show -->
          <div class="stat-cell flex-fill d-flex flex-column justify-content-center px-3 py-2">
            <span class="label-muted small font-weight-semibold text-uppercase">No-show</span>
            <div class="d-flex align-items-baseline mt-1" style="gap: 4px">
              <span class="stat-num">{{ formatNumber(d.stats.noShow.count) }}</span>
              <span v-if="d.stats.noShow.total != null" class="small text-muted">/ {{ formatNumber(d.stats.noShow.total) }} นัด</span>
              <span v-if="d.stats.noShow.pct != null" class="small text-muted">({{ d.stats.noShow.pct.toFixed(1) }}%)</span>
            </div>
            <div v-if="d.stats.noShow.stale > 0" class="small text-muted">ค้างสถานะ {{ formatNumber(d.stats.noShow.stale) }} นัด</div>
            <trend-badge
              v-if="d.stats.noShowPrevPct != null"
              class="mt-1"
              :pct="d.stats.noShowPrevPct"
            />
          </div>

          <!-- หมอทำเงินสูงสุด -->
          <div class="stat-cell flex-fill d-flex flex-column justify-content-center px-3 py-2">
            <span class="label-muted small font-weight-semibold text-uppercase">หมอทำเงินสูงสุด</span>
            <div class="mt-1" v-if="topDoctor">
              <span class="font-weight-bold" style="font-size: 15px">{{ topDoctorName }}</span>
            </div>
            <div class="mt-1" v-else>
              <span class="text-muted">–</span>
            </div>
            <div class="small text-muted mt-1" v-if="topDoctor">
              {{ formatNumber(topDoctor.cases) }} เคส
            </div>
          </div>
        </div>
      </b-card>

      <!-- ===== ROW 3: Revenue + Appointments Trend Chart ===== -->
      <b-card class="unit-card mb-2" body-class="d-flex flex-column p-3">
        <div class="d-flex justify-content-between align-items-start" style="gap: 12px">
          <div>
            <div class="font-weight-bold small">แนวโน้มเงินเข้า · สาขานี้</div>
            <div class="label-muted small">
              {{ trendSubLabel }}
            </div>
          </div>
          <div class="d-flex align-items-center small text-muted flex-wrap" style="gap: 10px">
            <span class="d-flex align-items-center" style="gap: 4px">
              <span class="legend-dot" style="background: #2196F3"></span>ค่ารักษา
            </span>
            <span v-if="hasDepositActivity" class="d-flex align-items-center" style="gap: 4px">
              <span class="legend-dot" style="background: #26C6DA"></span>รับมัดจำ
            </span>
            <span class="d-flex align-items-center" style="gap: 4px">
              <span class="legend-dot" style="background: #F59E0B"></span>นัด
            </span>
          </div>
        </div>
        <div class="mt-2" v-if="trendChartData">
          <bar-chart
            :chart-data="trendChartData"
            :options="trendChartOptions"
            :styles="{ position: 'relative', height: '220px' }"
          />
        </div>
        <div v-else class="text-center text-muted small py-4">ไม่มีข้อมูลแนวโน้ม</div>

        <!-- ชำระด้วยมัดจำเก่า — แยกออกจากแท่งด้านบน เพราะไม่ใช่เงินเข้าของช่วงนี้ -->
        <div v-if="depositUsedChartData" class="deposit-used-strip mt-3 pt-3">
          <div class="d-flex justify-content-between align-items-baseline" style="gap: 12px">
            <div class="small">
              <b>ชำระด้วยมัดจำเก่า</b>
              <span class="label-muted"> · แยกออกมา ไม่รวมในแท่งด้านบน</span>
            </div>
            <div class="label-muted small">รวม {{ formatNumber(d.hero.revenue.depositUsed) }} ฿</div>
          </div>
          <bar-chart
            :chart-data="depositUsedChartData"
            :options="depositUsedChartOptions"
            :styles="{ position: 'relative', height: '70px' }"
          />
        </div>
      </b-card>

      <!-- ===== ROW 4: BarChart ยูนิตรายชั่วโมง ===== -->
      <b-card
        v-if="d.hourlyUnits && d.hourlyUnits.unitCount"
        class="unit-card mb-2"
        body-class="d-flex flex-column p-3"
      >
        <div class="d-flex justify-content-between align-items-start mb-1">
          <div>
            <div class="font-weight-bold small">
              % การใช้ยูนิตรายชั่วโมง
              <span class="label-muted font-weight-normal">· เฉลี่ยทุกวันในช่วง · {{ d.hourlyUnits.unitCount }} ยูนิต</span>
            </div>
            <div class="label-muted small">
              เห็นว่าช่วงเวลาไหนเต็ม/ว่าง เพื่อจัดคิวนัด
            </div>
          </div>
          <div class="d-flex align-items-center small text-muted" style="gap: 10px">
            <span class="d-flex align-items-center" style="gap: 4px">
              <span class="legend-dot" style="background: #2196F3"></span>≥75% (เต็ม)
            </span>
            <span class="d-flex align-items-center" style="gap: 4px">
              <span class="legend-dot" style="background: #9bbcff"></span>50–74%
            </span>
            <span class="d-flex align-items-center" style="gap: 4px">
              <span class="legend-dot" style="background: #E4E9F1"></span>&lt;50% (ว่าง)
            </span>
          </div>
        </div>
        <!-- :styles sets the height on vue-chartjs's own wrapper div; without it
             the canvas has no fixed parent height and balloons past the box,
             clipping the bars + x-axis (maintainAspectRatio:false). -->
        <bar-chart
          :chart-data="unitChartData"
          :options="unitChartOptions"
          :styles="{ position: 'relative', height: '200px' }"
        />
      </b-card>

      <!-- ===== ROW 5: รายได้หมอ ===== -->
      <b-row class="mb-2">
        <b-col>
          <b-card class="h-100" body-class="d-flex flex-column p-3">
            <div class="d-flex justify-content-between align-items-start mb-2">
              <div>
                <div class="font-weight-bold small">รายได้ต่อหมอ <span class="label-muted font-weight-normal">· ในช่วง</span></div>
                <div class="label-muted small">เรียงตามรายได้ · % = สัดส่วนของสาขา</div>
              </div>
              <div class="d-flex align-items-center" style="gap: 12px" v-if="sortedDoctors.length">
                <span class="d-flex align-items-center small label-muted" style="gap: 4px">
                  <span class="legend-dot" style="background: linear-gradient(90deg, #2196f3, #5b8dff)"></span>รายได้
                </span>
                <span class="d-flex align-items-center small label-muted" style="gap: 4px">
                  <span class="legend-dot" style="background: linear-gradient(90deg, #26c6da, #5be0d0)"></span>DF
                </span>
                <span class="label-muted small">{{ sortedDoctors.length }} ท่าน</span>
              </div>
            </div>
            <div
              v-if="sortedDoctors.length"
              class="d-flex flex-column"
              style="gap: 8px; flex: 1"
            >
              <div v-for="(doc, i) in sortedDoctors" :key="doc.doctorId || i">
                <div class="d-flex justify-content-between align-items-baseline mb-1">
                  <span class="small font-weight-500">{{ doc.name }}</span>
                  <div class="d-flex align-items-baseline" style="gap: 8px">
                    <span class="small text-muted">{{ formatNumber(doc.cases) }} เคส</span>
                    <span class="small font-weight-bold">{{ formatNumber(doc.revenue) }} ฿</span>
                    <span class="small text-muted">DF {{ formatNumber(doc.dfShare) }} ฿</span>
                    <span class="small font-weight-bold text-primary-dark" style="min-width: 34px; text-align: right">
                      {{ doctorShare(doc.revenue) }}%
                    </span>
                  </div>
                </div>
                <div class="doctor-bars">
                  <div class="doctor-bar-track">
                    <div
                      class="doctor-bar-fill"
                      :style="{ width: doctorBarWidth(doc.revenue) + '%' }"
                    ></div>
                  </div>
                  <div class="doctor-bar-track">
                    <div
                      class="doctor-bar-fill doctor-bar-fill--df"
                      :style="{ width: doctorBarWidth(doc.dfShare) + '%' }"
                    ></div>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="text-muted small mt-2">ยังไม่มีข้อมูลทันตแพทย์ในช่วงนี้</div>
          </b-card>
        </b-col>
      </b-row>

      <!-- ===== ROW 6: Case Mix Dual Donut (full-width) ===== -->
      <b-row class="mb-2">
        <b-col>
          <b-card class="h-100" body-class="d-flex flex-column p-3">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <div>
                <div class="font-weight-bold small">สัดส่วนหัตถการ <span class="label-muted font-weight-normal">· ตามหมวด</span></div>
                <div class="label-muted small">{{ (period && period.prevPeriodLabel) ? ('เทียบ ' + period.prevPeriodLabel) : 'เทียบช่วงก่อน' }}</div>
              </div>
              <!-- Mode toggle -->
              <div class="bd-seg bd-seg--ghost">
                <button
                  type="button"
                  class="bd-seg-btn"
                  :class="{ on: caseMixMode === 'pct' }"
                  @click="caseMixMode = 'pct'"
                >รายได้ %</button>
                <button
                  type="button"
                  class="bd-seg-btn"
                  :class="{ on: caseMixMode === 'count' }"
                  @click="caseMixMode = 'count'"
                >จำนวน</button>
              </div>
            </div>

            <template v-if="caseMixItems && caseMixItems.length">
              <div class="d-flex flex-wrap" style="gap: 12px">
                <!-- Current period donut -->
                <div class="bd-casemix-col flex-fill" style="min-width: 0">
                  <div class="label-muted small text-center mb-1" style="font-size: 11px">
                    {{ rangeLabel || 'ช่วงนี้' }}
                  </div>
                  <div style="position: relative; height: 130px; width: 130px; margin: 0 auto">
                    <pie-chart :chart-data="caseMixChartData" :options="caseMixOptions" />
                  </div>
                  <div class="mt-2">
                    <div
                      v-for="(item, i) in caseMixItems"
                      :key="i"
                      class="d-flex justify-content-between align-items-center py-1"
                    >
                      <span class="d-flex align-items-center small" style="gap: 5px; min-width: 0">
                        <span class="legend-dot" :style="{ background: item.color }"></span>
                        <span class="text-truncate">{{ item.category }}</span>
                      </span>
                      <span class="small font-weight-bold flex-shrink-0">
                        {{ caseMixMode === 'count' ? item.count : item.pct + '%' }}
                      </span>
                    </div>
                  </div>
                </div>

                <!-- Previous period donut (always shown for comparison) -->
                <div class="bd-casemix-col flex-fill" style="min-width: 0">
                  <div class="label-muted small text-center mb-1" style="font-size: 11px">
                    {{ (period && period.prevPeriodLabel) || 'ช่วงก่อน' }}
                  </div>
                  <template v-if="prevCaseMixItems && prevCaseMixItems.length">
                    <div style="position: relative; height: 130px; width: 130px; margin: 0 auto">
                      <pie-chart :chart-data="caseMixChartDataPrev" :options="caseMixOptions" />
                    </div>
                    <div class="mt-2">
                      <div
                        v-for="(item, i) in prevCaseMixItems"
                        :key="i"
                        class="d-flex justify-content-between align-items-center py-1"
                      >
                        <span class="d-flex align-items-center small" style="gap: 5px; min-width: 0">
                          <span class="legend-dot" :style="{ background: item.color }"></span>
                          <span class="text-truncate">{{ item.category }}</span>
                        </span>
                        <span class="small font-weight-bold flex-shrink-0">
                          {{ caseMixMode === 'count' ? item.count : item.pct + '%' }}
                        </span>
                      </div>
                    </div>
                  </template>
                  <div v-else class="bd-casemix-empty">
                    <div class="bd-casemix-empty-ring"></div>
                    <div class="label-muted small mt-2">ไม่มีข้อมูลช่วงก่อน</div>
                  </div>
                </div>
              </div>
            </template>
            <div v-else class="text-muted small mt-2">ยังไม่มีข้อมูล case mix ในช่วงนี้</div>
          </b-card>
        </b-col>
      </b-row>

      <!-- ===== ROW 7: รายการรักษาทำเงินสูงสุด ===== -->
      <b-card class="mb-2" body-class="p-3">
        <div class="font-weight-bold small mb-2">
          รายการรักษาทำเงินสูงสุด
          <span class="label-muted font-weight-normal">· Top {{ (d.topTreatments || []).length }}</span>
        </div>
        <div
          v-if="d.topTreatments && d.topTreatments.length"
          class="d-flex flex-column"
          style="gap: 11px"
        >
          <div
            v-for="(t, i) in d.topTreatments"
            :key="t.treatmentId || i"
            class="treat-row"
          >
            <div class="treat-rank">{{ i + 1 }}</div>
            <div style="min-width: 0">
              <div class="d-flex justify-content-between align-items-baseline small mb-1" style="gap: 10px">
                <span class="text-truncate">{{ t.name }}</span>
                <span class="font-weight-bold flex-shrink-0">
                  ฿{{ formatNumber(t.revenue) }}
                  <span class="text-muted font-weight-normal" style="font-size: 11.5px">{{ formatNumber(t.count) }} เคส</span>
                </span>
              </div>
              <div class="treat-track">
                <div class="treat-fill" :style="{ width: treatBarWidth(t.revenue) + '%' }"></div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="text-muted small text-center py-4">ไม่มีข้อมูลรายการรักษา</div>
      </b-card>

      <!-- branchAnalysis is gated to branch roles; in the central drill-down
           (clinic-level viewers) that route would reject, so hide the link there -->
      <div class="text-right mt-2" v-if="!isCentralFlow">
        <b-link @click="goAnalysis">ดูบทวิเคราะห์เชิงลึก + AI ▸</b-link>
      </div>

    </template>
  </div>
  </div>
</template>

<script>
import moment from "moment";
import { formatNumber } from "@/utils/utils";
import { formatRange, isFullMonthRange } from "@/utils/dashboardPeriod";
import BarChart from "@/components/chart/BarChart.vue";
import PieChart from "@/components/chart/PieChart.vue";
import TrendBadge from "@/components/dashboard/TrendBadge.vue";
import TargetProgress from "@/components/dashboard/TargetProgress.vue";
import PeriodSelector from "@/components/dashboard/PeriodSelector.vue";

const CASE_MIX_COLORS = [
  "#2196F3",
  "#26C6DA",
  "#8B5CF6",
  "#F59E0B",
  "#EF4444",
  "#94A3B8",
  "#22C55E",
  "#F97316",
  "#EC4899",
  "#64748B",
];

export default {
  name: "BranchDashboard",
  components: { BarChart, PieChart, TrendBadge, TargetProgress, PeriodSelector },

  data() {
    return {
      d: null,
      error: null,
      loading: false,

      // Period data from PeriodSelector
      period: null,

      // Case mix mode toggle
      caseMixMode: "pct",
    };
  },

  computed: {
    clinicUrl() {
      return this.$route.params.clinicUrl;
    },
    branchUrl() {
      return this.$route.params.branchUrl;
    },
    isCentralFlow() {
      return this.$route.name === "CentralBranchDashboard";
    },
    branchName() {
      // In the central drill-down, getBranchInfo reflects the logged-in branch,
      // not the route's branch — prefer a name passed through navigation.
      if (this.isCentralFlow) {
        return this.$route.params.branchName || this.branchUrl;
      }
      const info = this.$store.getters["moduleBranch/getBranchInfo"];
      return (info && info.name) || this.branchUrl;
    },

    // Seed PeriodSelector from route query for drill-down preservation.
    // ClinicDashboard passes viewType (and optionally month/year/day) in the query.
    initialPeriod() {
      const q = this.$route.query || {};
      if (!q.viewType) return null;
      return {
        viewType: q.viewType,
        month: q.month || undefined,
        year: q.year || undefined,
        day: q.day || undefined,
        comparison: q.comparison || undefined,
      };
    },

    // ---- Range / comparison labels ----
    rangeLabel() {
      const r = this.d && this.d.range;
      const start = r && r.startDt ? r.startDt : (this.period && this.period.startDt);
      const end = r && r.endDt ? r.endDt : (this.period && this.period.endDt);
      return formatRange(start, end, this.period && this.period.viewType);
    },
    prevPeriodTag() {
      return this.period && this.period.prevPeriodLabel ? "vs " + this.period.prevPeriodLabel : "";
    },
    prevYearTag() {
      return this.period && this.period.prevYearLabel ? "vs " + this.period.prevYearLabel : "";
    },
    comparisonLabel() {
      if (!this.d || !this.d.comparison) return "";
      const comparison = this.period && this.period.comparison;
      const cmp = this.d.comparison[comparison];
      if (!cmp) return "";
      if (comparison === "prevYear") {
        return formatRange(cmp.startDt, cmp.endDt) + " (ปีก่อน)";
      }
      const start = moment.utc(cmp.startDt).local();
      const end = cmp.endDt ? moment.utc(cmp.endDt).local() : null;
      // เดือนเต็มเท่านั้นจึงพูดว่า "เดือนก่อน" — งวดปัจจุบันเทียบกับช่วงบางส่วน (1–9 มิ.ย.)
      if (isFullMonthRange(start, end)) {
        return "เดือนก่อน (" + start.locale("th").format("MMM") + " " + (start.year() + 543) + ")";
      }
      return formatRange(cmp.startDt, cmp.endDt);
    },
    comparisonTag() {
      const comparison = this.period && this.period.comparison;
      return comparison === "prevYear" ? this.prevYearTag : this.prevPeriodTag;
    },

    // มัดจำเป็นฟีเจอร์ที่หลายสาขาไม่ได้ใช้ — ซ่อนทุกช่องที่เกี่ยวข้องถ้าช่วงนี้ไม่มีความเคลื่อนไหว
    hasDepositActivity() {
      const r = this.d && this.d.hero && this.d.hero.revenue;
      return !!r && !!(r.depositReceived || r.depositUsed);
    },

    // ---- Hero comparison badges (active comparison first, mirrors mockup) ----
    revenueBadges() {
      return this.buildHeroBadges(this.d && this.d.hero && this.d.hero.revenue);
    },
    perCaseBadges() {
      return this.buildHeroBadges(this.d && this.d.hero && this.d.hero.perCase);
    },

    // ---- Patient split ----
    newPatientPct() {
      const total = this.d && this.d.stats && this.d.stats.patients && this.d.stats.patients.total;
      const newPt = this.d && this.d.stats && this.d.stats.patients && this.d.stats.patients.new;
      if (!total) return 0;
      return Math.round((newPt / total) * 100);
    },

    // ---- No-show / util vs clinic average (percentage-point diff) ----
    noShowVsClinicPct() {
      if (!this.d || !this.d.stats || !this.d.clinicAvg) return null;
      const p = this.d.stats.noShow && this.d.stats.noShow.pct;
      const avg = this.d.clinicAvg.noShowPct;
      if (p == null || avg == null) return null;
      return p - avg;
    },
    utilVsClinicPct() {
      if (!this.d || !this.d.clinicAvg) return null;
      const me = this.unitAvgPct;
      const avg = this.d.clinicAvg.utilPct;
      if (me == null || avg == null) return null;
      return me - avg;
    },
    // Pre-computed labels (avoid bare `<`/`<=` operators in template mustaches,
    // which the Vue template compiler mis-parses as opening tags).
    noShowVsClinicLabel() {
      const diff = this.noShowVsClinicPct;
      if (diff == null) return null;
      const better = diff <= 0; // lower no-show is better
      return {
        cls: better ? "up" : "down",
        text:
          (better ? "ดีกว่าเฉลี่ย " : "สูงกว่าเฉลี่ย +") +
          Math.abs(diff).toFixed(1),
      };
    },
    utilVsClinicLabel() {
      const diff = this.utilVsClinicPct;
      if (diff == null) return null;
      const higher = diff >= 0; // higher utilisation is better
      return {
        cls: higher ? "up" : "down",
        text:
          (higher ? "สูงกว่าเฉลี่ย +" : "ต่ำกว่าเฉลี่ย ") +
          Math.abs(diff).toFixed(0),
      };
    },

    // ---- Hourly units (avg occupancy across all days in range) ----
    unitAvgPct() {
      const h = this.d && this.d.hourlyUnits;
      if (!h || !h.hours || !h.hours.length) return 0;
      const sum = h.hours.reduce((s, x) => s + (x.avgOccupancyPct || 0), 0);
      return Math.round(sum / h.hours.length);
    },
    unitChartData() {
      const h = this.d && this.d.hourlyUnits;
      if (!h || !h.hours) return { labels: [], datasets: [] };
      return {
        labels: h.hours.map((x) => String(x.hour).padStart(2, "0") + ":00"),
        datasets: [
          {
            label: "การใช้งาน",
            data: h.hours.map((x) => x.avgOccupancyPct),
            backgroundColor: h.hours.map((x) => {
              const v = x.avgOccupancyPct || 0;
              return v >= 75 ? "#2196F3" : v >= 50 ? "#9bbcff" : "#E4E9F1";
            }),
            borderRadius: 4,
          },
        ],
      };
    },
    unitChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        tooltips: {
          callbacks: {
            label: (tooltipItem) => "ใช้งาน " + tooltipItem.yLabel + "%",
          },
        },
        scales: {
          xAxes: [
            {
              gridLines: { display: false },
              ticks: { fontColor: "#6B7280", fontSize: 10 },
            },
          ],
          yAxes: [
            {
              ticks: {
                min: 0,
                max: 100,
                stepSize: 25,
                fontColor: "#6B7280",
                fontSize: 10,
                callback: (v) => v + "%",
              },
              gridLines: { color: "#E5EAF0" },
              scaleLabel: {
                display: true,
                labelString: "% การใช้งาน",
                fontColor: "#6B7280",
                fontSize: 10,
              },
            },
          ],
        },
      };
    },

    // ---- Trend chart (revenue + appointments dual-axis) ----
    isHourlyTrend() {
      return !!(this.d && this.d.bucket === "hour");
    },
    isMonthlyTrend() {
      return !!(
        this.d &&
        this.d.trend &&
        this.d.trend.length &&
        this.d.trend[0].date &&
        this.d.trend[0].date.length === 7
      );
    },
    trendSubLabel() {
      if (this.isHourlyTrend) return "รายได้สาขานี้ · รายชั่วโมง";
      if (this.isMonthlyTrend) return "รายได้สาขานี้ · รายเดือน";
      return "รายได้สาขานี้ · รายวัน";
    },
    // แท่งซ้อน: ค่ารักษาที่เก็บเงินได้ + มัดจำที่รับใหม่ = เงินเข้าจริง (ความสูงรวมของแท่ง)
    // ส่วนที่จ่ายด้วยมัดจำเก่าไม่อยู่ในแท่ง — ไปอยู่ใน depositUsedChartData ด้านล่างแทน
    trendChartData() {
      if (!this.d || !this.d.trend || !this.d.trend.length) return null;
      const pts = this.d.trend;
      const labels = pts.map((p) => this.trendLabel(p.date));
      const datasets = [
        {
          label: "ค่ารักษา",
          data: pts.map((p) => p.treatmentCash || 0),
          backgroundColor: "#2196F3",
          stack: "rev",
          yAxisID: "y-rev",
        },
      ];
      if (this.hasDepositActivity) {
        datasets.push({
          label: "รับมัดจำ",
          data: pts.map((p) => p.depositReceived || 0),
          backgroundColor: "#26C6DA",
          stack: "rev",
          yAxisID: "y-rev",
        });
      }
      datasets.push({
        type: "line",
        label: "นัด",
        data: pts.map((p) => p.appointments || 0),
        backgroundColor: "transparent",
        borderColor: "#F59E0B",
        borderWidth: 1.8,
        fill: false,
        pointRadius: 0,
        pointHoverRadius: 4,
        lineTension: 0.35,
        yAxisID: "y-appt",
      });
      return { labels, datasets };
    },

    // แถบเล็กใต้กราฟ — โผล่เฉพาะช่วงที่มีการเอามัดจำเก่ามาตัดค่ารักษาจริง
    depositUsedChartData() {
      if (!this.d || !this.d.trend || !this.d.trend.length) return null;
      const pts = this.d.trend;
      if (!pts.some((p) => p.depositUsed)) return null;
      return {
        labels: pts.map((p) => this.trendLabel(p.date)),
        datasets: [
          {
            label: "ชำระด้วยมัดจำ",
            data: pts.map((p) => p.depositUsed || 0),
            backgroundColor: "#F59E0B",
          },
        ],
      };
    },

    depositUsedChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        tooltips: {
          callbacks: {
            label: (tooltipItem) => "ชำระด้วยมัดจำ: ฿" + formatNumber(tooltipItem.yLabel || 0),
          },
        },
        scales: {
          xAxes: [{ gridLines: { display: false }, ticks: { display: false } }],
          yAxes: [
            {
              gridLines: { display: false },
              ticks: {
                fontColor: "#6B7280",
                fontSize: 9,
                beginAtZero: true,
                maxTicksLimit: 2,
                callback: (v) => (v >= 1000 ? (v / 1000).toFixed(0) + "K" : v),
              },
            },
          ],
        },
      };
    },
    trendChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        elements: { line: { capBezierPoints: false } },
        tooltips: {
          mode: "index",
          intersect: false,
          callbacks: {
            label: (tooltipItem, data) => {
              const ds = data.datasets[tooltipItem.datasetIndex];
              if (ds.label === "นัด") return ds.label + ": " + tooltipItem.yLabel + " นัด";
              return ds.label + ": ฿" + formatNumber(tooltipItem.yLabel || 0);
            },
            // ยอดรวมของแท่ง = เงินเข้าจริงของวันนั้น (ไม่รวมเส้นนัด)
            footer: (items, data) => {
              const total = items
                .filter((i) => data.datasets[i.datasetIndex].label !== "นัด")
                .reduce((sum, i) => sum + Number(i.yLabel || 0), 0);
              return "เงินเข้าจริง: ฿" + formatNumber(total);
            },
          },
        },
        scales: {
          xAxes: [
            {
              stacked: true,
              gridLines: { display: false },
              ticks: { fontColor: "#6B7280", fontSize: 9, maxTicksLimit: 12, autoSkip: true },
            },
          ],
          yAxes: [
            {
              id: "y-rev",
              stacked: true,
              gridLines: { color: "#E5EAF0" },
              ticks: {
                fontColor: "#6B7280",
                fontSize: 10,
                beginAtZero: true,
                callback: (v) => {
                  if (v >= 1000000) return (v / 1000000).toFixed(1) + "M";
                  if (v >= 1000) return (v / 1000).toFixed(0) + "K";
                  return v;
                },
              },
            },
            {
              id: "y-appt",
              position: "right",
              stacked: false,
              gridLines: { display: false },
              ticks: {
                fontColor: "#F59E0B",
                fontSize: 9,
                beginAtZero: true,
                callback: (v) => (Number.isInteger(v) ? v : ""),
              },
            },
          ],
        },
      };
    },

    // ---- Doctors sorted by revenue descending ----
    sortedDoctors() {
      if (!this.d || !this.d.doctors) return [];
      return [...this.d.doctors].sort((a, b) => (b.revenue || 0) - (a.revenue || 0));
    },
    maxDoctorRevenue() {
      if (!this.sortedDoctors.length) return 1;
      return this.sortedDoctors[0].revenue || 1;
    },
    topDoctor() {
      return this.sortedDoctors.length ? this.sortedDoctors[0] : null;
    },
    topDoctorName() {
      const n = this.topDoctor && this.topDoctor.name;
      if (!n) return "";
      return n.replace(/^[^.]+\.\s*/, "").trim();
    },
    totalDoctorRevenue() {
      return this.sortedDoctors.reduce((s, doc) => s + (doc.revenue || 0), 0);
    },

    // ---- Case mix (donut) ----
    // Build a stable category→color map keyed by name so the same category gets
    // the same color in both the current and previous period donuts even when
    // their revenue-DESC sort orders differ.
    // Base order: current period (so current donut keeps its revenue-ranked palette).
    // Categories that only appear in the previous period continue the index past
    // the current set so they still get a distinct, stable color.
    caseMixColorByCategory() {
      const map = {};
      const current = (this.d && this.d.caseMix) || [];
      let idx = 0;
      for (const item of current) {
        if (!(item.category in map)) {
          map[item.category] = CASE_MIX_COLORS[idx % CASE_MIX_COLORS.length];
          idx++;
        }
      }
      const prev = (this.d && this.d.prevPeriodCaseMix) || [];
      for (const item of prev) {
        if (!(item.category in map)) {
          map[item.category] = CASE_MIX_COLORS[idx % CASE_MIX_COLORS.length];
          idx++;
        }
      }
      return map;
    },
    caseMixItems() {
      if (!this.d || !this.d.caseMix || !this.d.caseMix.length) return null;
      const colorMap = this.caseMixColorByCategory;
      return this.d.caseMix.map((item) => ({
        ...item,
        color: colorMap[item.category],
      }));
    },
    prevCaseMixItems() {
      if (!this.d || !this.d.prevPeriodCaseMix || !this.d.prevPeriodCaseMix.length) return null;
      const colorMap = this.caseMixColorByCategory;
      return this.d.prevPeriodCaseMix.map((item) => ({
        ...item,
        color: colorMap[item.category],
      }));
    },
    caseMixChartData() {
      const items = this.caseMixItems || [];
      return {
        labels: items.map((c) => c.category),
        datasets: [
          {
            data: items.map((c) => this.caseMixMode === "count" ? (c.count || 0) : (c.pct || 0)),
            backgroundColor: items.map((c) => c.color),
            borderWidth: 2,
            borderColor: "#fff",
          },
        ],
      };
    },
    caseMixChartDataPrev() {
      const items = this.prevCaseMixItems || [];
      return {
        labels: items.map((c) => c.category),
        datasets: [
          {
            data: items.map((c) => this.caseMixMode === "count" ? (c.count || 0) : (c.pct || 0)),
            backgroundColor: items.map((c) => c.color),
            borderWidth: 2,
            borderColor: "#fff",
          },
        ],
      };
    },
    caseMixOptions() {
      return {
        responsive: true,
        // Keep the donut square and sized by width only. With maintainAspectRatio
        // false the canvas tracks its (flex-fill) parent's height and balloons
        // past the 150px box, drifting below the legend.
        maintainAspectRatio: true,
        aspectRatio: 1,
        cutoutPercentage: 58,
        legend: { display: false },
        tooltips: {
          callbacks: {
            label: (tooltipItem, data) => {
              const label = data.labels[tooltipItem.index] || "";
              const val = data.datasets[0].data[tooltipItem.index];
              return this.caseMixMode === "count"
                ? label + ": " + val + " เคส"
                : label + ": " + val + "%";
            },
          },
        },
      };
    },

    // ---- Top treatments bar max ----
    maxTreatmentRevenue() {
      if (!this.d || !this.d.topTreatments || !this.d.topTreatments.length) return 1;
      return Math.max(...this.d.topTreatments.map((t) => t.revenue || 0)) || 1;
    },
  },

  methods: {
    onPeriodChange(p) {
      this.period = p;
      this.load();
    },

    async load() {
      if (!this.period) return;
      this.error = null;
      this.loading = true;
      try {
        this.d = await this.$store.dispatch("dashboard/fetchBranchDashboard", {
          clinicUrl: this.clinicUrl,
          branchUrl: this.branchUrl,
          startDt: this.period.startDt,
          endDt: this.period.endDt,
        });
      } catch (e) {
        this.error = e;
        this.$bvToast.toast("โหลดข้อมูลแดชบอร์ดไม่สำเร็จ", {
          variant: "danger",
          title: "ข้อผิดพลาด",
          autoHideDelay: 4000,
        });
      } finally {
        this.loading = false;
      }
    },

    goBackToClinic() {
      // Central drill-down always originates from the clinic overview, so return
      // there (works on hard refresh too, unlike router.back()).
      this.$router.push({
        name: "ClinicDashboard",
        params: { clinicUrl: this.clinicUrl },
      });
    },

    goAnalysis() {
      // branchAnalysis is a branch-mode route; navigate to it directly regardless
      // of whether we are in central or branch mode — both routes carry clinicUrl
      // and branchUrl params.  Note: if arriving via CentralBranchDashboard the
      // router guard will trigger requiresDataFetch for branchAnalysis; the guard
      // handles this transparently via the data-fetch path.
      this.$router.push({
        name: "branchAnalysis",
        params: { clinicUrl: this.clinicUrl, branchUrl: this.branchUrl },
      });
    },

    // active comparison first, both badges shown
    buildHeroBadges(metric) {
      if (!metric) return [];
      const comparison = this.period && this.period.comparison;
      const order =
        comparison === "prevPeriod"
          ? ["prevPeriod", "prevYear"]
          : ["prevYear", "prevPeriod"];
      const tags = { prevPeriod: this.prevPeriodTag, prevYear: this.prevYearTag };
      return order
        .filter((k) => Number.isFinite(metric[k + "Pct"]))
        .map((k, i) => ({
          pct: metric[k + "Pct"],
          tag: tags[k],
          active: i === 0,
        }));
    },

    // ---- Bar widths / shares ----
    doctorShare(revenue) {
      if (!this.totalDoctorRevenue) return 0;
      return Math.round((revenue / this.totalDoctorRevenue) * 100);
    },
    doctorBarWidth(revenue) {
      if (!this.maxDoctorRevenue) return 0;
      return Math.round((revenue / this.maxDoctorRevenue) * 100);
    },
    treatBarWidth(revenue) {
      return Math.round(((revenue || 0) / this.maxTreatmentRevenue) * 100);
    },

    // ---- Trend label mapping ----
    trendLabel(date) {
      if (!date) return "";
      if (date.length === 7) return moment(date, "YYYY-MM").locale("th").format("MMM");
      if (date.length === 10) return moment(date, "YYYY-MM-DD").locale("th").format("D MMM");
      // hourly: "YYYY-MM-DD HH" (length 13)
      return moment(date, "YYYY-MM-DD HH").format("HH:00");
    },

    // ---- Formatting ----
    formatNumber(val) {
      return formatNumber(val || 0);
    },
    formatPct(val) {
      return val == null ? "–" : val.toFixed(1) + "%";
    },
    formatRange,
  },

  watch: {
    // Vue Router reuses this instance when only :branchUrl changes — refetch.
    branchUrl() {
      this.d = null;
      this.load();
    },
  },
};
</script>

<style scoped>
/* ---- Brand tokens ---- */
.branch-dashboard {
  --bd-primary: #2196f3;
  --bd-primary-dark: #1976d2;
  --bd-primary-light: #e3f2fd;
  --bd-green: #22c55e;
  --bd-red: #ef4444;
  --bd-orange: #f59e0b;
  --bd-purple: #8b5cf6;
  --bd-bg: #f5f8fb;
  --bd-border: #e5eaf0;
  --bd-text-muted: #6b7280;
}

/* ---- Utilities ---- */
.text-green  { color: var(--bd-green)  !important; }
.text-red    { color: var(--bd-red)    !important; }
.text-primary-dark { color: var(--bd-primary-dark) !important; }
.font-weight-500 { font-weight: 500; }
.font-weight-semibold { font-weight: 600; }
.label-muted { color: var(--bd-text-muted); }

/* ---- Segmented control ---- */
.bd-seg {
  display: inline-flex;
  background: #fff;
  border: 1px solid #d2dae6;
  border-radius: 9px;
  padding: 3px;
}
.bd-seg--ghost {
  border-color: var(--bd-border);
}
.bd-seg-btn {
  font-size: 12px;
  font-weight: 600;
  color: var(--bd-text-muted);
  background: none;
  border: 0;
  padding: 5px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: 0.15s;
}
.bd-seg-btn:disabled { cursor: not-allowed; opacity: 0.6; }
.bd-seg-btn.on {
  background: var(--bd-primary);
  color: #fff;
}
.bd-seg--ghost .bd-seg-btn.on {
  background: var(--bd-primary-light);
  color: var(--bd-primary-dark);
}

/* ---- Hero cards ---- */
.hero-card {
  border: 1px solid var(--bd-border);
  border-radius: 12px;
}
.hero-card--feat {
  border: 0;
  background: linear-gradient(135deg, #1d3a73, #2a6df4);
  color: #fff;
}
.hero-card--feat .unit-baht,
.hero-card--feat .label-feat { color: #fff; }
.label-feat { color: #dbe6ff; opacity: 0.9; }
.hero-num-lg {
  font-size: clamp(28px, 3.2vw, 42px);
  font-weight: 700;
  line-height: 1;
}
.hero-num {
  font-size: clamp(20px, 2.4vw, 32px);
  font-weight: 700;
  line-height: 1;
}
.unit-baht { font-size: 1.1rem; font-weight: 600; opacity: 0.7; color: var(--bd-text-muted); }
.hero-divider { border-top: 1px solid var(--bd-border); }

/* ---- Hero comparison badges ---- */
.badges { display: flex; gap: 8px; flex-wrap: wrap; }
.hero-badge {
  display: inline-flex;
  align-items: baseline;
  gap: 5px;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 9px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.18);
  color: #eaf1ff;
}
.hero-badge.active { box-shadow: 0 1px 0 #fff inset; }
.hero-badge--plain {
  background: var(--bd-bg);
  border-color: var(--bd-border);
  color: var(--bd-text-muted);
}
.hero-badge--plain.active { box-shadow: 0 1px 0 var(--bd-primary) inset; }
.hero-badge-v.up { color: #7ff0c4; }
.hero-badge-v.down { color: #ffb3a5; }
.hero-badge--plain .hero-badge-v.up { color: var(--bd-green); }
.hero-badge--plain .hero-badge-v.down { color: var(--bd-red); }
.hero-badge-tag { font-size: 10px; font-weight: 600; opacity: 0.7; }

/* ---- Rank ring ---- */
.rank-ring { position: relative; width: 74px; height: 74px; flex-shrink: 0; }
.rank-ring svg { transform: rotate(-90deg); }
.rank-ctr {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  line-height: 1;
  font-weight: 700;
}
.rank-ctr b { font-size: 22px; }
.rank-ctr small { font-size: 10px; color: var(--bd-text-muted); font-weight: 600; }

/* ---- Clinic-avg comparison ---- */
.clinic-avg { display: flex; flex-direction: column; gap: 8px; }
.ca-item { font-size: 11.5px; color: var(--bd-text-muted); }
.ca-item b { display: block; font-size: 17px; color: #1f2a37; font-weight: 700; margin-top: 1px; }
.ca-me { font-size: 11px; font-weight: 600; }
.ca-me.up { color: var(--bd-green); }
.ca-me.down { color: var(--bd-red); }

/* ---- Hero revenue breakdown ---- */
.hero-breakdown {
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.22);
  display: flex;
  flex-direction: column;
  gap: 5px;
  font-size: 12.5px;
}
.hero-breakdown > div {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  color: #dbe6ff;
}
.hero-breakdown b { color: #fff; }

/* ---- Deposit-used strip under the trend chart ---- */
.deposit-used-strip { border-top: 1px dashed var(--bd-border); }

/* ---- Stat strip ---- */
.stat-card {
  border: 1px solid var(--bd-border);
  border-radius: 12px;
}
.stat-cell {
  min-width: 0;
}
.stat-cell--deposit { background: #f0f9ff; }
.divide-cols > * + * {
  border-left: 1px solid var(--bd-border);
}
.stat-num {
  font-size: clamp(17px, 1.9vw, 24px);
  font-weight: 700;
  line-height: 1;
}

/* ---- Unit chart card ---- */
.unit-card {
  border: 1px solid var(--bd-border);
  border-radius: 12px;
}

/* ---- Doctor bar ---- */
.doctor-bar-track {
  position: relative;
  border-radius: 999px;
  background: var(--bd-bg);
  height: 8px;
}
.doctor-bar-fill {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, #2196f3, #5b8dff);
  transition: width 0.4s ease;
}
.doctor-bars {
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.doctor-bar-fill--df {
  background: linear-gradient(90deg, #26c6da, #5be0d0);
}

/* ---- Top treatments ---- */
.treat-row {
  display: grid;
  grid-template-columns: 22px 1fr;
  align-items: center;
  gap: 12px;
}
.treat-rank {
  font-weight: 700;
  color: var(--bd-text-muted);
  font-size: 13px;
  text-align: center;
}
.treat-track {
  height: 7px;
  border-radius: 6px;
  background: var(--bd-bg);
  overflow: hidden;
}
.treat-fill {
  height: 100%;
  border-radius: 6px;
  background: linear-gradient(90deg, #2196f3, #5b8dff);
  transition: width 0.4s ease;
}

/* ---- Legend dot ---- */
.legend-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 2px;
  flex-shrink: 0;
}

/* ---- Case mix dual columns ---- */
.bd-casemix-col {
  min-width: 120px;
}
.bd-casemix-empty {
  text-align: center;
  padding-top: 6px;
}
.bd-casemix-empty-ring {
  width: 130px;
  height: 130px;
  margin: 0 auto;
  border-radius: 50%;
  border: 14px solid var(--bd-bg, #f5f8fb);
}
@media (max-width: 575px) {
  .bd-casemix-col { flex: 0 0 100%; }
}
</style>
